import {
  CirularWrapper,
  FilterContainerWrapper,
  GraphWraperDiv,
  GridWrapper,
  HexagonWrapper,
  LegendSection,
  MainWrapper,
} from "./hexagon.styled";
import HexagonCells from "./components";
import { useEffect, useRef, useState } from "react";
import PortalTooltip from "./tooltip";
import HexBinGraphTooltip from "./tooltip/tooltip_body";
import Legends from "./components/legend";
import ResizeHandlerHOC from "../../../graphs/utils/resizeHandlerHOC";
import { useQueryParams } from "../../../../hooks/useQueryParams";
import { setHospitalsDetails } from "../../../../Redux/app/app.actions";
import { resetSelectedState, setSpecificStateData, resetSpecificStateData } from "../../../../Redux/hospital/hospital.actions";
import { useDispatch, useSelector } from "react-redux";
import { views } from "../../../../Configs/config";
import { useQueryClient } from "@tanstack/react-query";
import { getHospitalsByUrl } from "../../../../services/apiService";
import { formatHospitalDetails } from "../../../../utils";
import { useFilters } from "../../../../hooks/useFilters";

const baseUrl = process.env.REACT_APP_BASE_URL;

const Hexagon = ({
  size = 25,
  data,
  rows = 8,
  columns = 13,
  legends = false,
  gap = 1,
  graphAreaRef,
  handleMouseEnter,
  // handleMouseClick,
  isClicked,
  setIsClicked,
  enableTooltip = false,
  labelFontSize = 12,
  labelFontWeight = 600,
  labelFontColor = "#fff",
  tooltipLabelFont,
  tooltipLabelWeight,
  cohortItems,
  hexagonType,
  regionItems,
  fontFamily,
  color,
  fontWeight,
  isLoading,
  resetParams,
  currentKpi,
  query,
  setQuery,
  loading,
  setShowSkelton,
  resetFlag,
  setResetFlag,
  // fetchTableViewData, // REMOVED - no longer needed
  // setFlag,
}) => {
  const [tooltipPos, setTooltipPos] = useState({
    left: 0,
    top: 0,
    isVisible: false,
    data: {},
  });
  const tooltipRef = useRef(null);
  const mainWrapperRef = useRef(null);
  const [filteredData, setFilteredData] = useState(false);
  const [legendClickedValue, setLegendClickedValue] = useState(false);
  const hexagonHeight = Math.floor(size * Math.sqrt(3));
  const { searchParams, setParams } = useQueryParams();
  const [clickedCellToolTipPosition, setClickedCellToolTipPosition] = useState({
    left: 0,
    top: 0,
    isVisible: false,
    data: {},
  });
  const [isAnyCellClicked, setIsAnyCellClicked] = useState(false);
  // Monotonic request ids used to ignore stale responses when the selection
  // changes rapidly (replaces the manual AbortControllers now that requests go
  // through the React Query cache).
  const legendRequestIdRef = useRef(0);
  const tableFilterRequestIdRef = useRef(0);
  const marketMap = {};
  data?.data?.forEach((market) => {
    const index = market.id - 1;
    const row = market?.row;
    const column = market?.column;
    const color = data?.colorMapping[market?.performance] || "#B3C8CF";
    const border = market?.border;
    marketMap[`${row}-${column}`] = market;
    marketMap[`${row}-${column}-name`] = market?.abbrev;
    marketMap[`${row}-${column}-color`] = color;
    marketMap[`${row}-${column}-data`] = market;
    marketMap[`${row}-${column}-border`] = border;
  });

  const filteredKey = filteredData && Object.keys(filteredData)[0];
  const filteredValues = filteredData ? filteredData[filteredKey] : [];

  const isRegionSelected = filteredKey === "region";
  const isCohortSelected = filteredKey === "cohort";

  const legendFilter = searchParams.get("legendFilter");

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { partnerExclusiveFlag, globalFilter, quickFilter } = useFilters();
  const { specificStateData } = useSelector((store) => store.hospital);

  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const legendFilter = searchParams.get("legendFilter");
    if (legendFilter) {
      // Restore the selected legend so the highlight border persists after the
      // component remounts (e.g. switching to table view and back).
      setLegendClickedValue(legendFilter);
      handleLegendFilterChange(legendFilter);
    } else {
      setLegendClickedValue(false);
    }
  }, [searchParams, globalFilter?.value]);

  // useEffect(() => {
  //   const legendFilter = searchParams.get("legendFilter");
  //   if (legendFilter) handleLegendFilterChange(legendFilter);
  // }, [searchParams]);

  // When Partner ESA is excluded, the partner legend item is hidden, so clear
  // any active partner legend selection to avoid an orphaned map highlight.
  useEffect(() => {
    if (partnerExclusiveFlag) return;
    if (
      legendClickedValue === "partner" ||
      searchParams.get("legendFilter") === "partner"
    ) {
      setParams((next) => {
        next.delete("state");
        next.delete("legendFilter");
      });
      setLegendClickedValue(false);
      dispatch(
        setHospitalsDetails({
          view: views.MAP,
          filterFlag: false,
          data: [],
        }),
      );
    }
  }, [partnerExclusiveFlag, legendClickedValue, searchParams]);

  function filterByPerformance(data, performanceLevels) {
    if (data && data.length > 0) {
      return data
        .filter((item) =>
          performanceLevels.includes(item.performance.toLowerCase()),
        )
        .map((item) => item.abbrev);
    } else {
      return [];
    }
  }

  const handleLegendFilterChange = async (value) => {
    const result =
      value === "partner"
        ? (data?.data || [])
            .filter((item) => item?.isPartner)
            .map((item) => item.abbrev)
        : filterByPerformance(data?.data, value);
    if (result && result.length == 0) return;
    // The legend selection is the single source of truth in the URL
    // (`legendFilter`); the resolved density-bucket states are derived data, so
    // they are NOT written to the URL. Other panels (LeftSection table
    // pagination/export) derive the same list via useResolvedStates and rebuild
    // the backend URL on demand, so nothing is persisted here.
    const apiUrl = `${baseUrl}/new_data/hospitals/filter?states=${result}&partnerExclusiveArea=${partnerExclusiveFlag}&globalFilter=${globalFilter?.value}&quickFilter=${quickFilter}`;

    const requestId = ++legendRequestIdRef.current;

    try {
      setShowSkelton(true);
      // Cached per resolved-states + filter set; React Query cancels stale
      // in-flight requests via the forwarded signal.
      const data = await queryClient.fetchQuery({
        queryKey: ["hospitals", "map-legend", apiUrl],
        queryFn: ({ signal }) => getHospitalsByUrl(apiUrl, signal),
        staleTime: 5 * 60 * 1000,
      });
      // Ignore if a newer legend selection superseded this request.
      if (requestId !== legendRequestIdRef.current) return;
      const hospitalDetailsArray = formatHospitalDetails(data?.data);
      dispatch(
        setHospitalsDetails({
          view: views.MAP,
          filterFlag: false,
          // isGraphCellClicked: true,
          totalCount: data?.total_count,
          data: hospitalDetailsArray,
        }),
      );
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching hospital data:", error.message);
      }
    } finally {
      if (requestId === legendRequestIdRef.current) setShowSkelton(false);
    }
  };

  const handleMouseEnterFn = (e, data) => {
    // const isFilterSelectedClicked =
    //   (isRegionSelected && filteredValues?.has(data?.region)) ||
    //   (isCohortSelected && filteredValues?.has(data.cohort?.toUpperCase()));
    // if (
    //   (isClicked?.data && isClicked?.data?.abbrev !== data?.abbrev) ||
    //   (legendClickedValue !== data?.performance && legendClickedValue) ||
    //   (filteredData && !isFilterSelectedClicked)
    // ) {
    //   return;
    // }

    // Always show tooltip on hover, regardless of whether a state is selected or not
    const svgClassName = `${data?.abbrev}-hexagon`;
    const svgElement = document.querySelector(`.${svgClassName}`);
    const tooltipElement = document?.querySelector(".tooltip_body");
    const parentElement = document?.querySelector(".MainContainer");

    let tooltipBoundary = { width: 140, height: 50 };

    if (tooltipElement) {
      const tooltipRect = tooltipElement.getBoundingClientRect();
      tooltipBoundary = tooltipRect;
    }

    if (svgElement) {
      // Get the bounding rectangle of the SVG element
      const svgRect = svgElement.getBoundingClientRect();
      const parentRect = parentElement.getBoundingClientRect();

      const tooltipOffset = size / 3; // Adjust the tooltip offset as needed

      const tooltipPositions = { top: 0, left: 0, position: "bottom" };

      if (data?.column === 0 || data?.column === 1) {
        tooltipPositions.top =
          svgRect.top +
          svgRect.height / 2 +
          tooltipBoundary?.height / 2 -
          parentRect.top;
        tooltipPositions.left =
          svgRect.left +
          svgRect.width +
          tooltipBoundary?.width / 1.6 -
          parentRect?.left;
        tooltipPositions.position = "left";
      } else if (data?.column === columns - 1) {
        tooltipPositions.top =
          svgRect.top +
          svgRect.height / 2 +
          tooltipBoundary?.height / 2 -
          parentRect.top;
        tooltipPositions.left =
          svgRect.left - tooltipBoundary?.width / 1.6 - parentRect?.left;
        tooltipPositions.position = "right";
      } else if (data?.row === 1 && data?.column === 11) {
        tooltipPositions.top =
          svgRect.top +
          svgRect.height / 2 +
          tooltipBoundary?.height / 2 -
          parentRect.top;
        tooltipPositions.left =
          svgRect.left - tooltipBoundary?.width / 1.6 - parentRect?.left;
        tooltipPositions.position = "right";
      } else {
        // Default position above the SVG element
        tooltipPositions.top = svgRect.top - parentRect.top - tooltipOffset;
        tooltipPositions.left =
          svgRect.left + svgRect.width / 2 - parentRect?.left;
      }

      // Set tooltip position and visibility
      setTooltipPos(() => ({
        ...tooltipPositions,
        isVisible: true,
        data: { ...data, position: tooltipPositions.position },
      }));
    }

    handleMouseEnter(e, data);
  };

  const handleMouseLeaveFn = () => {
    // Always hide the tooltip when not hovering
    setTooltipPos({ top: 0, left: 0, isVisible: false, data: {} });
  };

  useEffect(() => {
    if (isClicked?.data?.abbrev) {
      const { market, abbrev, value } = isClicked?.data;
      // The right panel fetches its own top-25 for the selected state, and the
      // table data is refreshed by the effect below, so we only need to keep the
      // selection (state/abbreviation) fresh here on filter changes.
      dispatch(
        setSpecificStateData({
          abbreviation: abbrev,
          hospitalCount: value,
          state: market,
        }),
      );
      setParams((next) => {
        next.set("state", abbrev);
      });
      setShowSkelton(false);
    }
  }, [partnerExclusiveFlag, globalFilter, quickFilter]);

  const fetchFilteredHospitalsData = async (state) => {
    const url = `${baseUrl}/new_data/hospitals/filter?states=${String(state)}&partnerExclusiveArea=${partnerExclusiveFlag}&globalFilter=${globalFilter?.value}${quickFilter ? `&quickFilter=${quickFilter}` : ""}`;

    const requestId = ++tableFilterRequestIdRef.current;

    try {
      const apiData = await queryClient.fetchQuery({
        queryKey: ["hospitals", "map-state", url],
        queryFn: ({ signal }) => getHospitalsByUrl(url, signal),
        staleTime: 5 * 60 * 1000,
      });
      // Ignore if a newer state selection superseded this request.
      if (requestId !== tableFilterRequestIdRef.current) return;
      const data = apiData.data || apiData;
      const totalCount = apiData.total_count || 0;
      const hospitalDetailsArray = formatHospitalDetails(data);
      dispatch(
        setHospitalsDetails({
          view: views.MAP,
          filterFlag: false,
          // isGraphCellClicked: true,
          data: hospitalDetailsArray,
          totalCount: totalCount,
        }),
      );
    } catch (error) {
      if (error.name !== "AbortError") {
        // swallow
      }
    }
  };

  useEffect(() => {
    const isStateSelected = Object.values(specificStateData)?.length;
    if (isStateSelected)
      fetchFilteredHospitalsData(specificStateData?.abbreviation);
  }, [partnerExclusiveFlag, globalFilter, quickFilter]);

  // Tracks the `state` value we have already restored, so we don't re-populate
  // a selection that is being torn down. Without this, a deselect (which clears
  // Redux and removes `state` in separate renders) could momentarily look like
  // "no selection + state=XX in URL" and wrongly re-add the state.
  const restoredStateRef = useRef(null);

  // Restore the single-state selection from the URL on (re)load: when `state`
  // holds an abbreviation but Redux has no selection yet (e.g. after a refresh),
  // repopulate specificStateData from the hex data so the map/table title shows
  // the state, and fetch its hospitals so the count is accurate.
  useEffect(() => {
    const selectedAbbrev = searchParams.get("state");
    if (!selectedAbbrev || legendFilter) {
      restoredStateRef.current = null;
      return;
    }
    // Already handled this exact state value (selected or restored) - don't
    // fight a deselect that is still propagating through the URL.
    if (restoredStateRef.current === selectedAbbrev) return;
    if (specificStateData && specificStateData.abbreviation === selectedAbbrev) {
      restoredStateRef.current = selectedAbbrev;
      return;
    }
    const market = (data?.data || []).find((m) => m.abbrev === selectedAbbrev);
    if (!market) return;
    restoredStateRef.current = selectedAbbrev;
    dispatch(
      setSpecificStateData({
        state: market.market,
        abbreviation: market.abbrev,
        hospitalCount: market.value,
      }),
    );
    fetchFilteredHospitalsData(selectedAbbrev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, data, specificStateData, legendFilter]);

  const handleMouseClickedFn = (e, data) => {
    // e.stopPropagation(); // Prevent event from bubbling up to MainWrapper

    // Check if the clicked cell is the same as the one already selected
    if (isClicked?.data?.market === data?.market) {
      // setFlag(false);
      dispatch(
        setHospitalsDetails({
          view: views.MAP,
          filterFlag: false,
          // isGraphCellClicked: false,
          data: [],
        }),
      );
      // fetchTableViewData(); // REMOVED - data fetching handled by LeftSection
      // Reset the clicked state and tooltip
      setIsClicked({ row: null, column: null, value: false, data: null });
      setTooltipPos({ top: 0, left: 0, isVisible: false, data: {} });

      // Reset the filters and search params
      setFilteredData(false);
      setParams((next) => {
        next.delete("legendFilter"); // Remove legend filter
        next.delete("state"); // Clear state selection
      });
      dispatch(resetSelectedState());
      dispatch(resetSpecificStateData()); // reset graph's cell selected state data

      return;
    }
    // setFlag(true);
    // Proceed with fetching hospital data if it's a different cell
    setLegendClickedValue(false);

    setFilteredData(false);
    setParams((next) => {
      next.delete("legendFilter"); // Remove legend filter
      next.set("state", data?.abbrev); // Set selected state (abbreviation)
    });

    if (
      Object.keys(specificStateData).length === 0 ||
      specificStateData.abbreviation !== data.abbrev
    ) {
      dispatch(
        setSpecificStateData({
          state: data.market,
          abbreviation: data.abbrev,
          hospitalCount: data.value,
        }),
      );
    }
    const state = data?.abbrev;
    fetchFilteredHospitalsData(state);
    // The right panel fetches its own top-25 for the selected state; the table
    // data is loaded by fetchFilteredHospitalsData above.
    setShowSkelton(false);

    // Same tooltip positioning logic
    if (isClicked?.data?.abbrev !== data?.abbrev) {
      const svgClassName = `${data?.abbrev}-hexagon`;
      const svgElement = document.querySelector(`.${svgClassName}`);
      const tooltipElement = document?.querySelector(".tooltip_body");
      const parentElement = document?.querySelector(".MainContainer");

      let tooltipBoundary = { width: 140, height: 50 };
      if (tooltipElement) {
        const tooltipRect = tooltipElement.getBoundingClientRect();
        tooltipBoundary = tooltipRect;
      }

      if (svgElement) {
        const svgRect = svgElement.getBoundingClientRect();
        const parentRect = parentElement.getBoundingClientRect();
        const tooltipOffset = size / 3;

        const tooltipPositions = { top: 0, left: 0, position: "bottom" };

        if (data?.column === 0 || data?.column === 1) {
          tooltipPositions.top =
            svgRect.top +
            svgRect.height / 2 +
            tooltipBoundary?.height / 2 -
            parentRect.top;
          tooltipPositions.left =
            svgRect.left +
            svgRect.width +
            tooltipBoundary?.width / 1.6 -
            parentRect?.left;
          tooltipPositions.position = "left";
        } else if (data?.column === columns - 1) {
          tooltipPositions.top =
            svgRect.top +
            svgRect.height / 2 +
            tooltipBoundary?.height / 2 -
            parentRect.top;
          tooltipPositions.left =
            svgRect.left - tooltipBoundary?.width / 1.6 - parentRect?.left;
          tooltipPositions.position = "right";
        } else if (data?.row === 1 && data?.column === 11) {
          tooltipPositions.top =
            svgRect.top +
            svgRect.height / 2 +
            tooltipBoundary?.height / 2 -
            parentRect.top;
          tooltipPositions.left =
            svgRect.left - tooltipBoundary?.width / 1.6 - parentRect?.left;
          tooltipPositions.position = "right";
        } else {
          // Default position above the SVG element
          tooltipPositions.top = svgRect.top - parentRect.top - tooltipOffset;
          tooltipPositions.left =
            svgRect.left + svgRect.width / 2 - parentRect?.left;
        }

        setTooltipPos(() => ({
          ...tooltipPositions,
          isVisible: true,
          data: { ...data, position: tooltipPositions.position },
        }));
        setClickedCellToolTipPosition({
          ...tooltipPositions,
          isVisible: true,
          data: { ...data, position: tooltipPositions.position },
        });
      }
    }
  };

  const renderTooltip = () => {
    return (
      enableTooltip && (
        <PortalTooltip
          isOpen={tooltipPos?.isVisible}
          resettToolTipPos={() => {
            setTooltipPos(() => {
              return { top: 0, left: 0 };
            });
          }}
          pos={tooltipPos}
          tooltipOffset={0}
          tooltipRef={tooltipRef}
        >
          <div
            className="tooltip"
            style={{
              position: "relative",
            }}
          >
            <HexBinGraphTooltip
              data={tooltipPos?.data}
              tooltipLabelFont={tooltipLabelFont}
              tooltipLabelWeight={tooltipLabelWeight}
              // color={tooltipPos?.data?.color}
            />
            <div
              className="tooltip-anchor"
              style={{
                position: "absolute",
                inset: "0px",
              }}
            ></div>
          </div>
        </PortalTooltip>
      )
    );
  };

  const handleLegendFn = (value) => {
    if (legendClickedValue == value) {
      // setFlag(false);
      setLegendClickedValue(false);
      // Clear any prior single-state selection; legend states are derived, not
      // stored in the URL.
      setParams((next) => {
        next.delete("state");
        next.delete("legendFilter");
      });
      dispatch(
        setHospitalsDetails({
          view: views.MAP,
          filterFlag: false,
          // isGraphCellClicked: true,
          data: [],
        }),
      );
    } else {
      // setFlag(true);
      setParams((next) => {
        next.delete("state");
        next.set("legendFilter", value);
      });
      setIsClicked({ row: null, column: null, value: false });
      setLegendClickedValue(value);
    }
    setTooltipPos({ top: 0, left: 0, isVisible: false, data: {} });
    dispatch(resetSelectedState());
    dispatch(resetSpecificStateData());
  };

  useEffect(() => {
    if (!isClicked?.value) {
      setTooltipPos({ top: 0, left: 0, isVisible: false, data: {} });
    }
  }, [isClicked?.value]);

  const handleReset = () => {
    // Check if either 'legendFilter' or a single-state selection is present
    if (searchParams.has("legendFilter") || searchParams.has("state")) {
      dispatch(
        setHospitalsDetails({
          view: views.MAP,
          filterFlag: false,
          // isGraphCellClicked: false,
          data: [],
        }),
      );
      // fetchTableViewData(); // REMOVED - data fetching handled by LeftSection
    }
    // setFlag(false);
    setLegendClickedValue(false);
    setIsClicked({ row: null, column: null, value: false });
    setTooltipPos({ top: 0, left: 0, isVisible: false, data: {} });
    setFilteredData(false);
    // Strips only the map selection (legend/state); other filters are kept.
    setParams((next) => {
      next.delete("legendFilter");
      next.delete("state");
    });
    setIsAnyCellClicked(false);
    setResetFlag(false);
    dispatch(resetSelectedState());
    dispatch(resetSpecificStateData()); // reset graph's cell selected state data
  };

  useEffect(() => {
    if (currentKpi) {
      handleReset();
    }
  }, [currentKpi]);

  // Global/logo reset: the URL was already cleared by the triggering navigation
  // (clicking the logo -> navigate("/")). Only sync the local + redux map state
  // and clear the flag. We must NOT write the URL here: a concurrent navigation
  // is in flight, so any write would race it and could re-add the very params
  // the reset just dropped.
  useEffect(() => {
    if (!resetFlag) return;
    setLegendClickedValue(false);
    setIsClicked({ row: null, column: null, value: false });
    setTooltipPos({ top: 0, left: 0, isVisible: false, data: {} });
    setFilteredData(false);
    setIsAnyCellClicked(false);
    dispatch(
      setHospitalsDetails({ view: views.MAP, filterFlag: false, data: [] }),
    );
    dispatch(resetSelectedState());
    dispatch(resetSpecificStateData());
    setResetFlag(false);
  }, [resetFlag]);

  useEffect(() => {
    const legendFilter = searchParams.get("legendFilter");
    if (isAnyCellClicked) setIsAnyCellClicked(false);
    if (legendFilter === "low") {
      data.colorMapping["low"] = "#FDDDD7";
      data.colorMapping["medium"] = "#F5F5F5";
      data.colorMapping["high"] = "#F5F5F5";
    } else if (legendFilter === "medium") {
      data.colorMapping["medium"] = "#F99C89";
      data.colorMapping["low"] = "#F5F5F5";
      data.colorMapping["high"] = "#F5F5F5";
    } else if (legendFilter === "high") {
      data.colorMapping["high"] = "#F96C50";
      data.colorMapping["low"] = "#F5F5F5";
      data.colorMapping["medium"] = "#F5F5F5";
    } else {
      data.colorMapping["low"] = "#FDDDD7";
      data.colorMapping["medium"] = "#F99C89";
      data.colorMapping["high"] = "#F96C50";
    }
  }, [searchParams.get("legendFilter")]);

  return (
    <MainWrapper ref={mainWrapperRef} className="US-MAP-HEX-GRPAH">
      {isLoading ? (
        <CirularWrapper>
          {/* <CircularProgress /> */}
          <div>loading...</div>
        </CirularWrapper>
      ) : (
        <>
          <GraphWraperDiv ref={graphAreaRef}>
            <GridWrapper
              columns={columns}
              rows={rows}
              size={size}
              hexagonHeight={hexagonHeight}
              gap={gap}
            >
              {Array?.from({ length: rows }).map((_, row) =>
                Array?.from({ length: columns }).map((_, column) => {
                  const market = marketMap[`${row}-${column}`];
                  const name = marketMap[`${row}-${column}-name`];
                  const color = marketMap[`${row}-${column}-color`];
                  const data = marketMap[`${row}-${column}-data`];
                  const border = marketMap[`${row}-${column}-border`];
                  return (
                    <HexagonWrapper
                      className="hex"
                      size={size}
                      key={`${row}-${column}`}
                    >
                      {market ? (
                        <HexagonCells
                          size={size}
                          color={color}
                          border={border}
                          data={data}
                          isFirstColumn={column === 0}
                          isSecondColumn={column % 2 === 0}
                          isSecondRows={row % 2 !== 0}
                          index={column}
                          legendClickedValue={legendClickedValue}
                          hexagonHeight={hexagonHeight}
                          row={row}
                          filteredData={filteredData}
                          column={column}
                          marketName={`${name || ""}`}
                          handleMouseEnter={handleMouseEnterFn}
                          handleMouseClick={handleMouseClickedFn}
                          handleMouseMove={handleMouseEnterFn}
                          handleMouseLeave={handleMouseLeaveFn}
                          handleReset={handleReset}
                          isClicked={isClicked}
                          labelFontSize={labelFontSize}
                          labelFontWeight={labelFontWeight}
                          labelFontColor={labelFontColor}
                          setIsClicked={setIsClicked}
                          // query={query}
                          query={""}
                          loading={loading}
                          isAnyCellClicked={isAnyCellClicked}
                          setIsAnyCellClicked={setIsAnyCellClicked}
                        />
                      ) : (
                        <div
                          style={{ width: "100%", height: "100%" }}
                          // onClick={handleReset}
                        ></div>
                      )}
                    </HexagonWrapper>
                  );
                }),
              )}
            </GridWrapper>
            {renderTooltip()}
          </GraphWraperDiv>
          {legends && (
            <LegendSection>
              <Legends
                data={data?.legend}
                partnerExclusiveFlag={partnerExclusiveFlag}
                size={7.5}
                labelFontSize={0.6875}
                handleLegendFn={handleLegendFn}
                legendClickedValue={legendClickedValue}
                LegendType={
                  hexagonType === "market_valuation" ? "type1" : "type2"
                }
                handleReset={handleReset}
                loading={loading}
              />
            </LegendSection>
          )}
        </>
      )}
    </MainWrapper>
  );
};

export default ResizeHandlerHOC(Hexagon);
